import { useLocation, useParams } from 'react-router'
import { OperationResult, useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  FeatureGroupsConfigFormValues,
  FeaturesConfigurationFeatureGroupsForm,
} from 'src/screens/administration/configuration-management/features-configuration/feature-groups/edit/FeaturesConfigurationFeatureGroupsForm'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { ROUTES } from 'src/routing/routes'
import {
  queryFeatureGroupConfigById,
  updateFeatureGroupConfigAndRelatedFeaturesByGroupId,
  updateFeatureGroupConfigById,
} from 'src/screens/administration/configuration-management/features-configuration/feature-groups/featureGroupsConfigQueries'
import {
  Feature_Config_Set_Input,
  Feature_Group_Config_Set_Input,
  Feature_Type_Config,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import {
  canUpdateFeatureGroupConfigStatus,
  fetchFeatureTypeById,
} from 'src/screens/administration/configuration-management/features-configuration/feature-groups/edit/FeaturesConfigurationFeatureGroupsFormUtils'
import { resolveFeaturesConfigRoot } from 'src/routing/routing-utils'

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

type UpdateFeatureGroupConfigResponse = OperationResult<{
  update_feature_group_config: Mutation_Root['update_feature_group_config']
  update_feature_config?: Mutation_Root['update_feature_config']
}>

const submitFeatureGroupConfigForm = () => {
  document.getElementById('feature-group-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const FeaturesConfigurationFeatureGroupsEditPage = () => {
  const { featureGroupId } = useParams()
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const { state } = useLocation()

  const [initialValues, setInitialValues] = useState<FeatureGroupsConfigFormValues | null>(null)
  const [featureTypes, setFeatureTypes] = useState<Feature_Type_Config[]>([])
  const originRef = useRef<OriginType>('SAVE')

  const onBackHandler = useCallback(() => {
    const processTypeFromState = state?.processType

    if (processTypeFromState !== null && processTypeFromState !== undefined) {
      navigate(resolveFeaturesConfigRoot(processTypeFromState))
    } else {
      navigate(ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatureGroups.params({}))
    }
  }, [navigate, state])

  const onSave = async () => {
    originRef.current = 'SAVE'
    submitFeatureGroupConfigForm()
  }

  const onSaveAndBack = async () => {
    originRef.current = 'SAVE_AND_BACK'
    submitFeatureGroupConfigForm()
  }

  const fetchFeatureGroupToEdit = useCallback(async () => {
    if (!featureGroupId) {
      throw new Error()
    }

    const { data, error } = await urqlClient
      .query<{ feature_group_config: Query_Root['feature_group_config'] }>(queryFeatureGroupConfigById, {
        featureGroupId,
      })
      .toPromise()

    if (error || !data?.feature_group_config[0]) {
      throw new Error('Resource not found')
    }

    const fetchedFeatureGroupConfig = data.feature_group_config[0]

    const editingFeatureGroupConfig: FeatureGroupsConfigFormValues = {
      enName: fetchedFeatureGroupConfig.names['en'],
      deName: fetchedFeatureGroupConfig.names['de'],
      frName: fetchedFeatureGroupConfig.names['fr'],
      itName: fetchedFeatureGroupConfig.names['it'],
      status: fetchedFeatureGroupConfig.active ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE,
      featureTypeId: fetchedFeatureGroupConfig.feature_type_id,
    }

    return { editingFeatureGroupConfig }
  }, [featureGroupId, urqlClient])

  const updateFeatureGroupConfigToActive = useCallback(
    async (featureGroupId: number, featureGroupConfigSetInput: Feature_Group_Config_Set_Input) => {
      return await urqlClient
        .mutation<{
          update_feature_group_config: Mutation_Root['update_feature_group_config']
        }>(updateFeatureGroupConfigById, {
          featureGroupId,
          featureGroupConfigSetInput,
        })
        .toPromise()
    },
    [urqlClient],
  )

  const updateFeatureGroupConfigToInactive = useCallback(
    async (featureGroupId: number, featureGroupConfigSetInput: Feature_Group_Config_Set_Input) => {
      const featureConfigSetInput: Feature_Config_Set_Input = {
        active: false,
      }

      return await urqlClient
        .mutation<{
          update_feature_group_config: Mutation_Root['update_feature_group_config']
          update_feature_config: Mutation_Root['update_feature_config']
        }>(updateFeatureGroupConfigAndRelatedFeaturesByGroupId, {
          featureGroupId,
          featureGroupConfigSetInput,
          featureConfigSetInput,
        })
        .toPromise()
    },
    [urqlClient],
  )

  const handleSubmitLocal = useCallback(
    async (values: FeatureGroupsConfigFormValues) => {
      if (featureGroupId == null || values.featureTypeId == null) {
        notificationService.operationFailed()
        return
      }

      if (!canUpdateFeatureGroupConfigStatus(values.featureTypeId, values.status, featureTypes)) {
        notificationService.error(getMessage('notification.edit.feature.group.status.invalid'))
        return
      }

      const isActiveSelected = values.status === CONFIGURATION_STATUS.ACTIVE
      const featureGroupConfigSetInput: Feature_Group_Config_Set_Input = {
        names: { de: values.deName, en: values.enName, fr: values.frName, it: values.itName },
        active: isActiveSelected,
      }

      let updateFeatureGroupConfigResponse: UpdateFeatureGroupConfigResponse
      const featureGroupIdParsed = parseInt(featureGroupId as string)
      if (isActiveSelected) {
        updateFeatureGroupConfigResponse = await updateFeatureGroupConfigToActive(
          featureGroupIdParsed,
          featureGroupConfigSetInput,
        )
      } else {
        updateFeatureGroupConfigResponse = await updateFeatureGroupConfigToInactive(
          featureGroupIdParsed,
          featureGroupConfigSetInput,
        )
      }

      const { data, error } = updateFeatureGroupConfigResponse
      if (!error && data?.update_feature_group_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setInitialValues(values)
        if (originRef.current === 'SAVE_AND_BACK') {
          onBackHandler()
        }
      } else {
        notificationService.operationFailed()
      }
    },
    [
      featureGroupId,
      featureTypes,
      getMessage,
      notificationService,
      onBackHandler,
      updateFeatureGroupConfigToActive,
      updateFeatureGroupConfigToInactive,
    ],
  )

  const initData = useCallback(async () => {
    try {
      const { editingFeatureGroupConfig } = await fetchFeatureGroupToEdit()
      setInitialValues(editingFeatureGroupConfig)

      const featureTypeId = editingFeatureGroupConfig.featureTypeId as number
      const { fetchedFeatureType } = await fetchFeatureTypeById(urqlClient, featureTypeId)()
      setFeatureTypes([fetchedFeatureType])
    } catch (e) {
      notificationService.operationFailed()
    }
  }, [fetchFeatureGroupToEdit, notificationService, urqlClient])

  useEffect(() => {
    initData()
  }, [initData])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.features.group.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
          <SaveButton origin="header" onClick={onSave} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <FeaturesConfigurationFeatureGroupsForm
              mode="EDIT"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              featureTypes={featureTypes}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
